/**
 * The design system resolved into a plain JS object React Native can consume.
 *
 * Spacing / radii / type scale / z-index are ported verbatim from tokens.ts (the CSS
 * custom properties). Shadows become RN shadow* props (+ elevation for Android, and
 * react-native-web maps them to box-shadow). Colors come from palette.ts keyed by mode.
 */
import { Platform } from 'react-native';
import { LIGHT, DARK, type Palette } from './palette';
import type { Theme as ThemeMode } from '../types';

export const font = {
  sans: Platform.select({
    web: '"IBM Plex Sans", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
    ios: 'System',
    android: 'sans-serif',
    default: 'System',
  }) as string,
  mono: Platform.select({
    web: '"IBM Plex Mono", ui-monospace, Menlo, monospace',
    ios: 'Menlo',
    android: 'monospace',
    default: 'monospace',
  }) as string,
};

export const fontSize = {
  xs: 11,
  sm: 12,
  md: 13,
  lg: 15,
  xl: 18,
  '2xl': 22,
  '3xl': 28,
} as const;

export const fontWeight = {
  regular: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
} as const;

/** 4px base spacing scale — keys match tokens.ts --sp-* (index, not px). */
export const spacing = {
  0: 0,
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40,
  12: 48,
  16: 64,
} as const;

export const radii = {
  1: 3,
  2: 5,
  3: 8,
  4: 12,
  5: 16,
  full: 999,
} as const;

export const zIndex = {
  base: 0,
  sticky: 20,
  overlay: 40,
  modal: 60,
  toast: 80,
  tooltip: 100,
} as const;

/** RN shadow presets. On iOS uses shadow*, on Android elevation, on web box-shadow (via RNW). */
function shadow(elevation: number, radius: number, opacity: number, offsetY: number) {
  return {
    shadowColor: '#101828',
    shadowOffset: { width: 0, height: offsetY },
    shadowOpacity: opacity,
    shadowRadius: radius,
    elevation,
  } as const;
}

export const shadows = {
  sm: shadow(1, 1, 0.04, 1),
  base: shadow(2, 6, 0.08, 2),
  md: shadow(4, 10, 0.1, 4),
  lg: shadow(8, 18, 0.14, 8),
};

export interface Theme {
  mode: ThemeMode;
  colors: Palette;
  font: typeof font;
  fontSize: typeof fontSize;
  fontWeight: typeof fontWeight;
  spacing: typeof spacing;
  radii: typeof radii;
  zIndex: typeof zIndex;
  shadows: typeof shadows;
}

export function makeTheme(mode: ThemeMode): Theme {
  return {
    mode,
    colors: mode === 'dark' ? DARK : LIGHT,
    font,
    fontSize,
    fontWeight,
    spacing,
    radii,
    zIndex,
    shadows,
  };
}
